import { useState } from 'react'
import { Link, useRouterState } from '@tanstack/react-router'
import {
  Box, Container, Typography, Drawer, IconButton, useMediaQuery, useTheme,
} from '@mui/material'
import { MenuBook } from '@mui/icons-material'
import { SiteNav } from '../site/SiteNav'
import { SiteFooter } from '../site/SiteFooter'
import { DOCS_NAV, isDocPageActive } from './nav'

function Sidebar({ current, onNavigate }: { current: string; onNavigate?: () => void }) {
  return (
    <Box component="nav" aria-label="Documentation">
      {DOCS_NAV.map((section) => (
        <Box key={section.label} sx={{ mb: 3.5 }}>
          <Typography sx={{
            fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.1em',
            textTransform: 'uppercase', color: 'var(--text-faint)', mb: 1.25,
          }}>
            {section.label}
          </Typography>
          {section.pages.map((page) => {
            const active = isDocPageActive(page.path, current)
            return (
              <Box
                key={page.path}
                component={Link}
                to={page.path}
                onClick={onNavigate}
                aria-current={active ? 'page' : undefined}
                sx={{
                  display: 'block', py: 0.6, pl: 1.5, ml: '-1px',
                  // Active state is a rule, not a pill — same vocabulary as
                  // the rest of the site.
                  borderLeft: active ? '2px solid var(--inflow)' : '1px solid var(--border-soft)',
                  color: active ? 'var(--text)' : 'var(--text-secondary)',
                  fontWeight: active ? 600 : 400,
                  fontSize: '0.905rem',
                  textDecoration: 'none',
                  '&:hover': { color: 'var(--text)' },
                }}
              >
                {page.title}
              </Box>
            )
          })}
        </Box>
      ))}
    </Box>
  )
}

/**
 * Two-column docs shell: persistent sidebar on desktop, drawer on mobile.
 *
 * Uses the marketing SiteNav/SiteFooter unchanged so /docs is visually the
 * same site rather than a bolted-on subdomain look.
 */
export function DocsLayout({ children }: { children: React.ReactNode }) {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const [open, setOpen] = useState(false)
  const current = useRouterState({ select: (s) => s.location.pathname })

  return (
    <Box sx={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <SiteNav />

      {!isDesktop && (
        <Box sx={{
          borderBottom: '1px solid var(--border-soft)',
          position: 'sticky', top: 57, zIndex: 9,
          background: 'color-mix(in srgb, var(--bg) 88%, transparent)',
          backdropFilter: 'blur(12px)',
        }}>
          <Container maxWidth="lg">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75 }}>
              <IconButton size="small" onClick={() => setOpen(true)} aria-label="Open documentation menu">
                <MenuBook fontSize="small" />
              </IconButton>
              <Typography variant="caption" sx={{ color: 'var(--text-faint)' }}>
                Documentation
              </Typography>
            </Box>
          </Container>
        </Box>
      )}

      <Container maxWidth="lg" sx={{ pt: { xs: 4, md: 7 }, pb: { xs: 8, md: 12 } }}>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '212px minmax(0, 1fr)' },
          gap: { md: 7 },
          alignItems: 'start',
        }}>
          {isDesktop && (
            <Box sx={{ position: 'sticky', top: 96 }}>
              <Sidebar current={current} />
            </Box>
          )}
          <Box component="main" sx={{ minWidth: 0 }}>{children}</Box>
        </Box>
      </Container>

      <Drawer
        anchor="left" open={open} onClose={() => setOpen(false)}
        sx={{ '& .MuiDrawer-paper': { width: 268, p: 3 } }}
      >
        <Sidebar current={current} onNavigate={() => setOpen(false)} />
      </Drawer>

      <SiteFooter />
    </Box>
  )
}
